import { gql, useQuery } from '@apollo/client';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle,
    Button,
    Center,
    Spinner,
    Stack,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';

import { useCurrent } from '@/hooks/useCurrent';
import { useMentorshipContext } from '@/modules/mentorship/common/context/useMentorshipContext';

const memberRouteFromPath = (
  path: string
): { role?: 'mentor' | 'mentee'; module?: string } => {
  const match = path.match(
    /^\/mentorship\/(mentor|mentee)(?:\/([^/?#]+))?/
  );
  return {
    role: match?.[1] as 'mentor' | 'mentee' | undefined,
    module: match?.[2],
  };
};

const WORKSPACE_ACCESS_QUERY = gql`
  query MentorshipRouteWorkspaceAccess($role: String!) {
    myMentorshipWorkspaces(role: $role) {
      _id
      viewerAccess(role: $role) {
        state
        canRead
        reason
      }
    }
  }
`;

const WORKSPACE_COLLABORATION_MODULES = new Set([
  'chat',
  'goals',
  'tasks',
  'sessions',
  'resources',
  'discussions',
  'mentees',
  'peers',
  'mentor',
  'incoming-requests',
  'activity-timeline',
]);

const GuardMessage: React.FC<{
  title: string;
  description: string;
  action?: React.ReactNode;
}> = ({ title, description, action }) => (
  <Center minH="65vh" px="5">
    <Alert
      status="warning"
      variant="subtle"
      alignItems="flex-start"
      maxW="680px"
      borderRadius="xl"
      p={{ base: '5', md: '6' }}
    >
      <AlertIcon mt="1" />
      <Stack spacing="3" flex="1">
        <AlertTitle fontSize="lg">{title}</AlertTitle>
        <AlertDescription color="gray.700">{description}</AlertDescription>
        {action}
      </Stack>
    </Alert>
  </Center>
);

export const MentorshipRouteGuard: React.FC = ({ children }) => {
  const router = useRouter();
  const current = useCurrent();
  const path = router.asPath.split('?')[0];
  const isAdminMentorshipRoute = path.startsWith('/admin/mentorship');
  const isMemberMentorshipRoute = path === '/mentorship' || path.startsWith('/mentorship/');
  const isProfileMentorshipRoute =
    path === '/me/profile' && router.query.showMentorship === '1';
  const isMentorshipRoute =
    isAdminMentorshipRoute ||
    isMemberMentorshipRoute ||
    isProfileMentorshipRoute;
  const mentorshipEnabled =
    current.tenant?.featureFlags?.mentorshipEnabled === true;
  const memberRoute = isMemberMentorshipRoute
    ? memberRouteFromPath(path)
    : {};
  const role = memberRoute.role;
  const mentorship = useMentorshipContext(
    isMemberMentorshipRoute &&
      mentorshipEnabled &&
      Boolean(current.user?._id)
  );
  const roleApplied =
    role === 'mentor'
      ? mentorship.context?.mentorApplied === true
      : role === 'mentee'
      ? mentorship.context?.menteeApplied === true
      : false;
  const roleApproved =
    role === 'mentor'
      ? mentorship.context?.mentorApproved === true
      : role === 'mentee'
      ? mentorship.context?.menteeApproved === true
      : false;
  const roleOnboarded =
    role === 'mentor'
      ? mentorship.context?.mentorOnboarded === true
      : role === 'mentee'
      ? mentorship.context?.menteeOnboarded === true
      : false;
  const workspaceId =
    typeof router.query.workspaceId === 'string'
      ? router.query.workspaceId
      : undefined;
  const requestedTab =
    typeof router.query.tab === 'string' ? router.query.tab : undefined;
  const collaborationArea =
    WORKSPACE_COLLABORATION_MODULES.has(memberRoute.module ?? '') ||
    (memberRoute.module === 'workspace' &&
      WORKSPACE_COLLABORATION_MODULES.has(requestedTab ?? ''));
  const chatArea =
    memberRoute.module === 'chat' ||
    (memberRoute.module === 'workspace' && requestedTab === 'chat');
  const workspaceAccessQuery = useQuery<{
    myMentorshipWorkspaces: Array<{
      _id: string;
      viewerAccess: { state: string; canRead: boolean; reason: string };
    }>;
  }>(WORKSPACE_ACCESS_QUERY, {
    variables: { role },
    skip:
      !isMemberMentorshipRoute ||
      !mentorshipEnabled ||
      !roleApproved ||
      !workspaceId,
    fetchPolicy: 'network-only',
  });

  if (!isMentorshipRoute) return <>{children}</>;

  if (current.loading || !router.isReady) {
    return (
      <Center minH="65vh">
        <Spinner color="primary.500" size="lg" />
      </Center>
    );
  }

  if (!mentorshipEnabled) {
    return (
      <GuardMessage
        title="Mentorship is not enabled"
        description="Mentorship is not available for this association. Contact your association administrator or TradeWing Support if you believe you should have access."
        action={
          <Button
            as="a"
            href="mailto:support@tradewing.com"
            alignSelf="flex-start"
            colorScheme="primary"
            variant="outline"
          >
            Contact support
          </Button>
        }
      />
    );
  }

  if (!isMemberMentorshipRoute || !role || path === '/mentorship') {
    return <>{children}</>;
  }

  if (mentorship.loading) {
    return (
      <Center minH="65vh">
        <Spinner color="primary.500" size="lg" />
      </Center>
    );
  }

  if (mentorship.error) {
    return (
      <GuardMessage
        title="Mentorship access could not be verified"
        description="We could not verify your Mentorship access. Refresh the page or contact support if the problem continues."
      />
    );
  }

  const preOnboardingModules = new Set([
    'dashboard',
    'profile',
    'programs',
    'notifications',
  ]);
  const canAccessRole =
    roleApproved ||
    (roleApplied && preOnboardingModules.has(memberRoute.module ?? 'dashboard'));

  if (!canAccessRole) {
    const roleLabel = role === 'mentor' ? 'Mentor' : 'Mentee';
    const awaitingApproval = roleApplied && !roleApproved;
    return (
      <GuardMessage
        title={
          awaitingApproval
            ? `${roleLabel} access is awaiting approval`
            : `${roleLabel} access is not available`
        }
        description={
          awaitingApproval
            ? `Your ${roleLabel.toLowerCase()} profile must be approved before you can use this Mentorship area. You can continue viewing Programs while the application is reviewed.`
            : `You do not currently have access to the ${roleLabel} Mentorship view. Submit the ${roleLabel.toLowerCase()} profile application, or contact your association administrator if you believe this is incorrect.`
        }
        action={
          <Button
            alignSelf="flex-start"
            colorScheme="primary"
            onClick={() =>
              router.push(
                awaitingApproval
                  ? `/mentorship/${role}/programs`
                  : `/me/profile?showMentorship=1&mentorshipRole=${role.toUpperCase()}`
              )
            }
          >
            {awaitingApproval ? 'View Programs' : 'Open Mentorship profile'}
          </Button>
        }
      />
    );
  }

  if (
    !roleOnboarded &&
    !preOnboardingModules.has(memberRoute.module ?? 'dashboard')
  ) {
    return (
      <GuardMessage
        title="Available after Workspace onboarding"
        description="This area becomes available after you are onboarded to a Mentorship Workspace. Follow your Application Journey on the Dashboard for current progress and next steps."
        action={
          <Button
            alignSelf="flex-start"
            colorScheme="primary"
            onClick={() => router.push(`/mentorship/${role}/dashboard`)}
          >
            View Application Journey
          </Button>
        }
      />
    );
  }

  if (workspaceId && roleApproved) {
    if (workspaceAccessQuery.loading) {
      return (
        <Center minH="65vh">
          <Spinner color="primary.500" size="lg" />
        </Center>
      );
    }

    if (workspaceAccessQuery.error) {
      return (
        <GuardMessage
          title="Workspace access could not be verified"
          description="We could not verify your access to this Mentorship Workspace. Refresh the page or contact support if the problem continues."
        />
      );
    }

    const workspace = workspaceAccessQuery.data?.myMentorshipWorkspaces.find(
      item => item._id === workspaceId
    );
    if (!workspace) {
      return (
        <GuardMessage
          title="Workspace access is not available"
          description="This Workspace is not part of your current Mentor or Mentee relationships. Contact your association administrator if you believe this is incorrect."
          action={
            <Button
              alignSelf="flex-start"
              colorScheme="primary"
              onClick={() => router.push(`/mentorship/${role}/workspace`)}
            >
              Open my Workspaces
            </Button>
          }
        />
      );
    }

    if (
      (collaborationArea && !workspace.viewerAccess.canRead) ||
      (chatArea && workspace.viewerAccess.reason === 'participant_frozen')
    ) {
      return (
        <GuardMessage
          title="Workspace tools are not available"
          description="This Workspace currently blocks participant access. Open the Workspace overview to see its current access status."
          action={
            <Button
              alignSelf="flex-start"
              colorScheme="primary"
              onClick={() =>
                router.push(
                  `/mentorship/${role}/workspace?workspaceId=${encodeURIComponent(
                    workspaceId
                  )}`
                )
              }
            >
              View Workspace status
            </Button>
          }
        />
      );
    }
  }

  return <>{children}</>;
};
